import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaClock,
  FaCar,
  FaFileInvoice,
  FaHistory,
  FaTimes,
  FaUser,
  FaEnvelope,
  FaPhoneAlt,
} from "react-icons/fa";
import api from "../api/Api";
import Loader from "../components/history_loader";

// --- BULLETPROOF TIME FORMATTER ---
const formatDateTime = (dateStr, timeStr) => {
  if (!dateStr) return "—";

  const dateObj = new Date(dateStr);
  const formattedDate = dateObj.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  let formattedTime = "";
  if (timeStr) {
    const timeString = String(timeStr);
    if (timeString.includes("T")) {
      const timeObj = new Date(timeString);
      formattedTime = timeObj.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } else {
      const cleanTime = timeString
        .padStart(4, "0")
        .replace(/(\d{2})(\d{2})/, "$1:$2");
      const [hours, minutes] = cleanTime.split(":");
      if (hours !== undefined && minutes !== undefined) {
        const tempDate = new Date();
        tempDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        formattedTime = tempDate.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      }
    }
  }

  return formattedTime ? `${formattedDate} · ${formattedTime}` : formattedDate;
};

export default function MyBookings() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("upcoming");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("customerToken");
      const response = await api.get("/bookingRoutes/customerTrips", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const mappedData = response.data.map((trip) => {
        // --- 1. BASE STATUS LOGIC ---
        let statusStr = "Pending";
        if (trip.enq_status === 1) statusStr = "Confirmed";
        if (trip.enq_status === 2) statusStr = "Expired";
        if (trip.enq_status === 3) statusStr = "Revised";

        // --- 2. CANCELLATION OVERRIDE LOGIC ---
        let actualStatus = statusStr;
        if (trip.is_cancelled === 1) {
          // Admin confirmed cancellation -> Hard "Cancelled"
          actualStatus = "Cancelled";
        } else if (Number(trip.cancel_request) === 1) {
          // Customer requested, admin hasn't approved yet -> "CancelPending"
          actualStatus = "CancelPending";
        }

        // --- 3. CORRECT EXACT DATE LOGIC ---
        let rideDateTime = new Date();
        if (trip.exact_ride_datetime) {
          rideDateTime = new Date(trip.exact_ride_datetime);
        } else if (trip.Journey_date || trip.arrive_dt) {
          rideDateTime = new Date(trip.Journey_date || trip.arrive_dt);
        }

        return {
          id: trip.enq_id,
          referenceNo: trip.reference_no,
          enqRefNo: trip.enqref_no,

          // Fetch both dates from DB
          enquiryDate: trip.enquiry_date || trip.enq_createddate,
          confirmationDate: trip.confirmation_date || trip.conf_date,

          Journey_date: trip.Journey_date || trip.arrive_dt,
          rideDateTime: rideDateTime,

          // Exact time or pickup time dynamically
          time:
            trip.exact_time_string ||
            trip.Pickup_time ||
            trip.arrive_time ||
            "00:00",

          status: actualStatus,
          carName: trip.taxi_car_name || "Assigned Cab",
          carImage: trip.carmodel_image
            ? `https://www.patratravels.com/admin/image/useridntimg/${trip.carmodel_image}`
            : "/no-car.png",
          date: new Date(
            trip.arrive_dt || trip.Journey_date,
          ).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          }),
          pickup: trip.pickup_location || trip.pickup_city || "Not Specified",
          drop: trip.dropoff_location || "Not Specified",
          price: Number(trip.final_price) || 0,
          driver: trip.driver_name || null,
          customerName: trip.gustcust_name || "Guest",
          customerEmail: trip.guestemail_id || "N/A",
          customerPhone: trip.guestmob_no || "N/A",
        };
      });

      setBookings(mappedData);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load your trips.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "Confirmed":
        return { backgroundColor: "#dcfce7", color: "#16a34a" }; // Light Green bg, Dark Green text
      case "Expired":
        return { backgroundColor: "rgba(255,255,255,0.2)", color: "#e2e8f0" };
      case "Revised":
        return { backgroundColor: "#fef3c7", color: "#d97706" };
      case "Cancelled":
        return { backgroundColor: "#fee2e2", color: "#dc2626" };
      case "CancelPending":
        return { backgroundColor: "#fff1f2", color: "#e11d48", border: "1px solid #fecdd3" };
      default:
        // Pending status (like in your image)
        return { backgroundColor: "rgba(255, 255, 255, 0.2)", color: "#ffffff" }; 
    }
  };

  // --- CHANGED TO "Cancel on Review" ---
  const getStatusLabel = (status) => {
    if (status === "CancelPending") return "Cancel on Review";
    return status;
  };

  const handleCancelClick = (e, booking) => {
    e.stopPropagation();
    setSelectedBooking(booking);
    setShowCancelModal(true);
  };

  const confirmCancellation = async () => {
    setCancelling(true);
    try {
      const token = localStorage.getItem("customerToken");
      const response = await api.post(
        "/bookingRoutes/cancelEnquiry",
        { enq_id: selectedBooking.id, reason: "Requested by customer" },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success) {
        setBookings((prev) =>
          prev.map((b) =>
            b.id === selectedBooking.id
              ? { ...b, status: "CancelPending" } // Sets to Cancel on Review until admin approves
              : b,
          ),
        );
      }
    } catch (err) {
      alert("Could not cancel booking. Please try again.");
    } finally {
      setCancelling(false);
      setShowCancelModal(false);
      setSelectedBooking(null);
    }
  };

  const filteredBookings = bookings.filter((b) => {
    const now = new Date();
    const isFutureRide = b.rideDateTime >= now;

    if (activeTab === "upcoming") {
      return (
        (b.status === "Confirmed" ||
          b.status === "Pending" ||
          b.status === "CancelPending") &&
        isFutureRide
      );
    }

    // Past rides gets everything else (Completed, Cancelled, Expired, Revised, or dates in the past)
    return (
      b.status === "Completed" ||
      b.status === "Cancelled" ||
      b.status === "Expired" ||
      b.status === "Revised" ||
      !isFutureRide
    );
  });

  return (
    <div className="d-flex flex-column min-vh-100 bg-light font-sans overflow-hidden">
      <div
        className="container-fluid flex-grow-1 p-0 pb-4"
        style={{ maxWidth: "800px", margin: "0 auto" }}
      >
        {/* --- SOFT GRADIENT HEADER WITH TABS --- */}
        <div
          className="bg-white shadow-sm mb-4 border-bottom border-light"
          style={{
            background: "linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)",
          }}
        >
          <div className="px-3 pb-3 pt-2">
            <div className="bg-white p-1 rounded-pill d-flex w-100 border shadow-sm">
              <button
                className={`btn flex-fill rounded-pill py-2 fw-bold transition-all btn-native-press ${activeTab === "upcoming" ? "bg-dark shadow-sm text-white" : "btn-light border-0 text-muted"}`}
                style={{ fontSize: "0.85rem" }}
                onClick={() => setActiveTab("upcoming")}
              >
                Upcoming
              </button>
              <button
                className={`btn flex-fill rounded-pill py-2 fw-bold transition-all btn-native-press ${activeTab === "history" ? "bg-dark shadow-sm text-white" : "btn-light border-0 text-muted"}`}
                style={{ fontSize: "0.85rem" }}
                onClick={() => setActiveTab("history")}
              >
                Past Rides
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="d-flex flex-column justify-content-center align-items-center w-100 mt-5 pt-5">
            <Loader />
          </div>
        ) : error ? (
          <div className="text-center py-5 mt-4 mx-3 bg-white rounded-4 border border-light shadow-sm">
            <div className="bg-danger bg-opacity-10 text-danger rounded-circle d-inline-flex p-3 mb-3">
              <FaTimes size={24} />
            </div>
            <h6 className="fw-bold text-dark mb-2">
              Oops! Something went wrong
            </h6>
            <p className="text-muted small mb-4">{error}</p>
            <button
              className="btn btn-outline-danger rounded-pill px-4 fw-bold btn-native-press"
              onClick={fetchBookings}
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="px-3">
            {filteredBookings.length > 0 ? (
              filteredBookings.map((item, index) => (
                <div
                  key={item.id}
                  className="card border-0 shadow-sm animate-slide-up mb-4"
                  style={{
                    background: "linear-gradient(135deg, #9965c4 0%, #4e2545 100%)", 
                    borderRadius: "20px",
                    overflow: "hidden",
                    animationDelay: `${index * 0.05}s`,
                    color: "#ffffff"
                  }}
                >
                  {/* --- 1. CARD HEADER --- */}
                  <div className="p-3 pb-2 d-flex justify-content-between align-items-start">
                    <div className="d-flex flex-column pe-2">
                      <span className="fw-bold mb-1" style={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "1px", color: "rgba(255,255,255,0.85)" }}>
                        Booking ID
                      </span>
                      <span className="fw-bold text-white mb-1" style={{ fontSize: "1.1rem" }}>
                        {item.referenceNo || item.enqRefNo}
                      </span>

                      {/* Date Logic */}
                      {item.status === "Confirmed" && item.confirmationDate ? (
                        <span className="fw-bold" style={{ fontSize: "0.75rem", color: "#bef264" }}>
                          Confirmed: {new Date(item.confirmationDate).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: true })}
                        </span>
                      ) : item.enquiryDate ? (
                        <span className="fw-medium" style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.85)" }}>
                          Booked: {new Date(item.enquiryDate).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: true })}
                        </span>
                      ) : null}
                    </div>

                    <div className="d-flex flex-column align-items-end flex-shrink-0">
                      <span className="text-white fw-bold mb-2 text-end" style={{ fontSize: "0.85rem" }}>
                        {formatDateTime(item.Journey_date, item.time)}
                      </span>
                      {/* Custom White Badge for Orange Background */}
                      <span
                        className="badge rounded-pill px-3 py-2 fw-bold d-flex align-items-center gap-2 shadow-sm"
                        style={{ 
                          backgroundColor: "#ffffff", 
                          color: item.status === "Cancelled" ? "#dc2626" : "#c2410c", 
                          fontSize: "0.75rem" 
                        }}
                      >
                        {item.status === "Confirmed" && (
                          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#16a34a" }} />
                        )}
                        {getStatusLabel(item.status)}
                      </span>
                    </div>
                  </div>

                  {/* --- 2. PASSENGER BOX (Separate Distinct Inset) --- */}
                  <div className="px-3 pt-2 pb-3">
                    <div 
                      className="d-flex align-items-center gap-3 p-3 rounded-4" 
                      style={{ 
                        background: "rgba(0, 0, 0, 0.15)", // Darker inset creates depth on bright orange
                        border: "1px solid rgba(255,255,255,0.1)"
                      }}
                    >
                      <div className="bg-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 shadow-sm" style={{ width: "42px", height: "42px", color: "#c2410c" }}>
                        <FaUser size={18} />
                      </div>
                      <div className="flex-grow-1 overflow-hidden">
                        <div className="fw-bold mb-1" style={{ fontSize: "0.6rem", letterSpacing: "1px", textTransform: "uppercase", color: "rgba(255,255,255,0.7)" }}>
                          PASSENGER
                        </div>
                        <div className="fw-bold text-white text-truncate mb-1" style={{ fontSize: "0.95rem" }}>
                          {item.customerName}
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          <span className="fw-semibold d-flex align-items-center gap-1" style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.9)" }}>
                            <FaPhoneAlt size={10} style={{ opacity: 0.7 }} /> {item.customerPhone}
                          </span>
                          <span className="fw-semibold d-flex align-items-center gap-1 text-truncate" style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.9)" }}>
                            <FaEnvelope size={10} style={{ opacity: 0.7 }} /> {item.customerEmail}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* --- 3. ROUTE TIMELINE --- */}
                  <div className="px-3 position-relative mb-3">
                    {item.drop !== "Not Specified" && (
                      <div
                        className="position-absolute"
                        style={{ left: "21px", top: "16px", bottom: "16px", borderLeft: "2px dashed rgba(255,255,255,0.4)", zIndex: 0 }}
                      />
                    )}
                    
                    {/* Pickup */}
                    <div className="d-flex position-relative mb-3">
                      <div className="mt-1 me-3 bg-white z-index-1 flex-shrink-0 shadow-sm" style={{ width: "14px", height: "14px", borderRadius: "50%", border: "3px solid #4ade80" }} />
                      <div className="flex-grow-1">
                        <div className="fw-bold mb-1" style={{ fontSize: "0.6rem", letterSpacing: "1px", textTransform: "uppercase", color: "rgba(255,255,255,0.85)" }}>PICKUP</div>
                        <div className="fw-bold text-white text-truncate" style={{ fontSize: "0.9rem", maxWidth: "280px" }}>{item.pickup}</div>
                      </div>
                    </div>
                    
                    {/* Drop-off */}
                    {item.drop !== "Not Specified" && (
                      <div className="d-flex position-relative">
                        <div className="mt-1 me-3 bg-white z-index-1 flex-shrink-0 shadow-sm" style={{ width: "14px", height: "14px", borderRadius: "2px", border: "3px solid #f87171" }} />
                        <div className="flex-grow-1">
                          <div className="fw-bold mb-1" style={{ fontSize: "0.6rem", letterSpacing: "1px", textTransform: "uppercase", color: "rgba(255,255,255,0.85)" }}>DROP-OFF</div>
                          <div className="fw-bold text-white text-truncate" style={{ fontSize: "0.9rem", maxWidth: "280px" }}>{item.drop}</div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* --- 4. CAR & FARE SECTION (Below Dashed Line) --- */}
                  <div className="px-3">
                    <div 
                      className="d-flex justify-content-between align-items-center py-3" 
                      style={{ borderTop: "1px dashed rgba(255, 255, 255, 0.4)" }}
                    >
                      <div className="fw-bold text-white d-flex align-items-center gap-2" style={{ fontSize: "0.95rem" }}>
                        <FaCar size={16} style={{ opacity: 0.8 }} /> {item.carName}
                      </div>
                      <div className="text-end flex-shrink-0">
                        <span className="d-block fw-bold mb-1" style={{ fontSize: "0.6rem", letterSpacing: "1px", color: "rgba(255,255,255,0.85)" }}>
                          TOTAL FARE
                        </span>
                        <div className="fw-bold text-white" style={{ fontSize: "1.25rem" }}>
                          ₹{item.price.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* --- 5. CARD FOOTER ACTIONS --- */}
                  <div className="p-3 d-flex gap-2" style={{ background: "rgba(0, 0, 0, 0.08)" }}>
                    {(item.status === "Confirmed" || item.status === "Pending") && (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/enquiry/${item.enqRefNo}`); }}
                        className="btn flex-fill rounded-pill fw-bold py-2 native-press"
                        style={{ 
                          fontSize: "0.9rem", 
                          backgroundColor: "rgba(255, 255, 255, 0.15)", // Translucent white
                          color: "#ffffff", 
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                          backdropFilter: "blur(10px)" // Creates the frosted glass effect
                        }}
                      >
                        View Enquiry
                      </button>
                    )}

                    {item.status === "CancelPending" && (
                      <div 
                        className="flex-fill d-flex align-items-center justify-content-center gap-2 rounded-pill py-2 shadow-sm" 
                        style={{ background: "#ffffff", fontSize: "0.85rem", fontWeight: 700, color: "#e11d48" }}
                      >
                        ⏳ Cancel on Review
                      </div>
                    )}

                    {(item.status === "Completed" || item.status === "Cancelled" || item.status === "Expired" || item.status === "Revised") && (
                      <>
                        {item.status === "Completed" && (
                          <button
                            className="btn flex-fill rounded-pill fw-bold py-2 native-press shadow-sm d-flex align-items-center justify-content-center gap-2"
                            style={{ 
                              fontSize: "0.85rem", 
                              backgroundColor: "rgba(255, 255, 255, 0.2)", 
                              color: "#ffffff", 
                              border: "1px solid rgba(255, 255, 255, 0.3)" 
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const base = Math.round(item.price * 0.85);
                              const taxAndFees = Math.round(item.price * 0.05);
                              const driverfee = Math.round(item.price * 0.1);
                              navigate("/invoice", { state: { trip: { bookingId: item.referenceNo, date: item.date, pickup: item.pickup, drop: item.drop, totalFare: item.price, baseFare: base, taxes: taxAndFees, driver: driverfee, discount: 0 } } });
                            }}
                          >
                            <FaFileInvoice /> Invoice
                          </button>
                        )}
                        <button
                          className="btn flex-fill rounded-pill fw-bold py-2 native-press shadow-sm"
                          style={{ fontSize: "0.85rem", backgroundColor: "#ffffff", color: "#c2410c", border: "none" }}
                          onClick={(e) => { e.stopPropagation(); navigate("/"); }}
                        >
                          Book Again
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))
            ) : (
              /* --- EMPTY STATE --- */
              <div className="text-center py-5 mt-4 rounded-4 bg-white border shadow-sm animate-fade-in" style={{ borderColor: "#f1f5f9" }}>
                <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: "70px", height: "70px", color: "#f97316" }}>
                  <FaHistory size={24} style={{ opacity: 0.5 }} />
                </div>
                <h6 className="fw-bold text-dark mb-1">No {activeTab} rides</h6>
                <p className="text-muted small px-4">When you book a ride, it will show up here.</p>
                <button
                  className="btn rounded-pill px-4 mt-2 fw-bold shadow-sm native-press"
                  onClick={() => navigate("/book-ride")}
                  style={{ backgroundColor: "#f97316", color: "#ffffff", border: "none" }}
                >
                  Book a Ride
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cancel Bottom Sheet */}
      {showCancelModal && (
        <div
          className="modal-backdrop-custom"
          onClick={() => !cancelling && setShowCancelModal(false)}
        >
          <div
            className="modal-content-custom d-flex flex-column"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-100 d-flex justify-content-center pt-3 pb-2">
              <div
                className="bg-secondary rounded-pill"
                style={{ width: "40px", height: "5px", opacity: 0.3 }}
              ></div>
            </div>
            <div className="p-4 pt-2 text-center">
              <div className="bg-danger bg-opacity-10 rounded-circle d-inline-flex p-3 mb-3 border border-danger border-opacity-25">
                <FaTimes className="text-danger" size={28} />
              </div>
              <h5 className="fw-bold text-dark mb-2">Cancel this ride?</h5>
              <p className="text-muted small mb-4 px-2">
                Are you sure you want to cancel booking{" "}
                <b className="text-dark">{selectedBooking?.referenceNo}</b>?{" "}
                Cancellation policies may apply.
              </p>
              <div className="d-flex flex-column gap-2">
                <button
                  onClick={confirmCancellation}
                  disabled={cancelling}
                  className="btn btn-danger w-100 fw-bold py-3 rounded-pill btn-native-press shadow-sm"
                >
                  {cancelling ? "Sending Request..." : "Yes, Cancel Ride"}
                </button>
                <button
                  onClick={() => setShowCancelModal(false)}
                  disabled={cancelling}
                  className="btn btn-light text-dark w-100 fw-bold py-3 rounded-pill border border-light btn-native-press"
                >
                  Keep Ride
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
                .transition-all { transition: all 0.2s ease; }
                .z-index-1000 { z-index: 1000; }
                .z-index-1 { z-index: 1; }
                .tracking-tight { letter-spacing: -0.5px; }
                .btn-native-press { transition: transform 0.1s ease, box-shadow 0.1s ease; }
                .btn-native-press:active { transform: scale(0.97); box-shadow: none !important; opacity: 0.9; }
                .border-dashed { border-top: 1px dashed #dee2e6 !important; }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes slideUpFade {
                    from { transform: translateY(20px); opacity: 0; }
                    to   { transform: translateY(0);    opacity: 1; }
                }
                .animate-slide-up { opacity: 0; animation: slideUpFade 0.4s ease-out forwards; }
                .modal-backdrop-custom {
                    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0,0,0,0.5); z-index: 1050;
                    display: flex; justify-content: center; align-items: flex-end;
                    animation: fadeIn 0.2s ease-out;
                }
                .modal-content-custom {
                    background: white; width: 100%; max-width: 600px;
                    border-radius: 24px 24px 0 0;
                    padding-bottom: max(env(safe-area-inset-bottom), 16px);
                    animation: slideUp 0.3s cubic-bezier(0.175, 0.885, 0.32, 1);
                }
                @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
                @media (min-width: 601px) {
                    .modal-backdrop-custom { align-items: center; }
                    .modal-content-custom { border-radius: 20px; padding-bottom: 0; }
                }
            `}</style>
    </div>
  );
}